export interface MatchRosterEntry {
  id: string;
  name: string;
  role: string;
  number?: number;
  isLibero?: boolean;
  /** Reception or defense libero of a two-libero team (VOL-163). */
  liberoRole?: 'reception' | 'defense';
}

export type MatchKind = 'OWN' | 'SCOUTING';

export type MatchPerspective = 'home' | 'away';

/** Réponse de `PATCH /matches/:id/player-visibility` (VOL-178). */
export interface MatchPlayerVisibilityResponse {
  id: string;
  playerVisibility: boolean | null;
  visibleToPlayers: boolean;
}

export interface VideoUploadUrlResponse {
  uploadUrl: string;
  key: string;
  playbackUrl: string;
  contentType: string;
  expiresInSeconds: number;
  maxFileSizeBytes: number;
}

export type VideoExportStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';

/** Montage vidéo d'un match (galerie de la page Vidéo), encodé en tâche de fond. */
export interface VideoExport {
  id: string;
  name: string;
  status: VideoExportStatus;
  progressPct: number | null;
  segmentCount: number;
  durationMs: number | null;
  sizeBytes: number | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  error: string | null;
  createdByName: string | null;
  canDelete: boolean;
  /** The chosen logo had gone before the render (photo changed): montage without it. Optional during a deploy. */
  logoDropped?: boolean;
  createdAt: string;
  completedAt: string | null;
  /** Planned deletion (2 months, VOL-332); null while video is free. Optional during a deploy. */
  expiresAt?: string | null;
}

export interface VideoExportList {
  items: VideoExport[];
  quota: { used: number; max: number };
  /** Planned deletion of the match video stored with us (1 year, VOL-332); null for an external link. */
  sourceVideoExpiresAt?: string | null;
}

/** What one player of the match's team opened (VOL-235); dates are the last opening. */
export interface PlayerViews {
  userId: string;
  name: string | null;
  email: string;
  videoSeenAt: string | null;
  statsSeenAt: string | null;
  exportsSeenAt: Record<string, string>;
}

export type PlayerViewsArea = 'video' | 'stats';

export interface MatchViews {
  players: PlayerViews[];
  /** Whether players can open each area of this match (team rule + match visibility). */
  access: Record<PlayerViewsArea, boolean>;
}

export type MatchViewKind = 'video' | 'stats';

export type LogoCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface VideoExportLogo {
  id: string;
  url: string;
  source: 'team' | 'library';
}

export interface CreateVideoExportPayload {
  name: string;
  titleCard?: boolean;
  logo?: { url: string; corner: LogoCorner };
  segments: { startMs: number; endMs: number; caption?: string }[];
}

export type InputSurface = 'touch' | 'keyboard';

export interface CaptureOptions {
  serviceType: boolean;
  attackType: boolean;
  middleBlockerCall: boolean;
  reversal: boolean;
  zones: boolean;
}

export interface MatchOpponentTeamSummary {
  id: string;
  name: string;
  gender?: string | null;
  /** Maillot domicile, hexadécimal « #rrggbb ». */
  jerseyColor?: string | null;
  /** Maillot extérieur ; à défaut, le maillot domicile sert partout. */
  jerseyColorAway?: string | null;
  /** Saison de la fiche adverse (« YYYY/YYYY+1 »). */
  season?: string | null;
}

export interface MatchSummary {
  id: string;
  date: string;
  opponent: string;
  location?: string;
  championship?: string;
  season?: string;
  /**
   * Coaches printed on the P2 (VOL-209), in the frame of the scores: ours / the opponent's, or
   * home / away for a scouted match. Absent from a backend older than VOL-209.
   */
  coachName?: string | null;
  assistantCoachName?: string | null;
  opponentCoachName?: string | null;
  opponentAssistantCoachName?: string | null;
  scoreUs: number;
  scoreThem: number;
  isFinished: boolean;
  currentSetNumber?: number;
  currentSetScoreUs?: number;
  currentSetScoreThem?: number;
  teamId?: string;
  /** `season` distingue deux équipes homonymes de saisons différentes. */
  team?: {
    id: string;
    name: string;
    season?: string | null;
    jerseyColor?: string | null;
    jerseyColorAway?: string | null;
    /** Team rule for players (VOL-178); absent = visible. */
    playersSeeOwnMatches?: boolean;
    playersSeeScoutingMatches?: boolean;
    /** Read-only team, over the plan quota (VOL-188). */
    locked?: boolean;
    /** Coach's player badges for the live court (VOL-113); null = defaults. */
    playerBadgeSettings?: PlayerBadgeSettings | null;
  };
  /** `actions` (active ones) comes with the list only, and not from a backend older than VOL-209. */
  _count?: { sets: number; actions?: number };
  /**
   * Sets enregistrés — présents sur le détail (`GET /matches/:id`), absents de
   * la liste. Sert au pré-remplissage de la modale de clôture.
   */
  sets?: { setNumber: number; scoreUs: number; scoreThem: number }[];
  matchMode?: 'live' | 'video';
  enabledActions?: string[];
  inputSurface?: InputSurface;
  captureOptions?: CaptureOptions | null;
  matchRoster?: MatchRosterEntry[];
  /** Effectif de l'équipe adverse (saisie deux équipes), ordre de la compo de départ. */
  opponentRoster?: MatchRosterEntry[];
  setsToWinMatch?: number;
  /**
   * Équipe statée en détail : `OWN` (la nôtre) ou `OPPONENT` (l'adversaire seul).
   * Distinct de `kind`, qui dit si le match est le nôtre ou un scouting.
   */
  statTarget?: 'OWN' | 'OPPONENT' | string;
  kind?: MatchKind | string;
  /**
   * Surcharge de la règle de l'équipe pour les joueurs (VOL-178) : `null` suit
   * la règle, `true` / `false` force l'affichage.
   */
  playerVisibility?: boolean | null;
  /** Visible par les joueurs, règle et surcharge appliquées (calculé par l'API). */
  visibleToPlayers?: boolean;
  homeOpponentTeamId?: string | null;
  awayOpponentTeamId?: string | null;
  homeOpponentTeam?: MatchOpponentTeamSummary | null;
  awayOpponentTeam?: MatchOpponentTeamSummary | null;
  videoStartOffsetMs?: number | null;
  videoUrl?: string | null;
  /**
   * Provenance des timecodes vidéo (VS-16) : `'import'` (DVW), `'capture'`
   * (position du lecteur figée en saisie) ou `'wallclock'` (heure de saisie).
   * En `'capture'` le timecode est une position absolue et l'offset vaut 0.
   */
  videoTimecodeSource?: 'import' | 'capture' | 'wallclock' | null;
}

/**
 * Métadonnées de pagination retournées par l'API backend.
 *
 * Miroir strict de `PaginatedMatchesMetaDto`
 * (Volley_Stats_Backend/src/features/match/dto/match-response.dto.ts).
 */
export interface PaginatedMatchesMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Réponse typée du endpoint `GET /matches` (liste paginée).
 *
 * Miroir strict de `PaginatedMatchesDto`. Le contrat est unique :
 * le backend renvoie toujours `{ data, meta }`, jamais un tableau brut.
 */
export interface PaginatedMatchesResponse {
  data: MatchSummary[];
  meta: PaginatedMatchesMeta;
}

/**
 * Query params de pagination acceptés par `GET /matches`.
 * Miroir strict de `PaginationDto` (common/dto/pagination.dto.ts).
 */
export interface MatchesPaginationQuery {
  page?: number;
  limit?: number;
  /** Filtre saison ("YYYY/YYYY+1"). */
  season?: string;
}

/**
 * Réponse du endpoint `GET /seasons`.
 * Miroir de `SeasonsResponse` (Volley_Stats_Backend/src/features/seasons).
 */
export interface SeasonsResponse {
  /** Saison courante déduite de la date ("YYYY/YYYY+1"). */
  current: string;
  /** Saisons visibles par l'utilisateur, de la plus récente à la plus ancienne. */
  all: string[];
}

export interface Club {
    id: string;
    name: string;
    city: string;
}

export const Role = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CLUB_ADMIN: 'CLUB_ADMIN',
  COACH: 'COACH',
  PLAYER: 'PLAYER',
} as const;

export type Role = typeof Role[keyof typeof Role];

export interface UserProfile {
    id: string;
    email: string;
    name: string;
    role: Role;
    clubId?: string;
    club?: Club;
}

export interface Player {
  id: string;
  name: string;
  number: number;
  role: string;
  isLibero: boolean;
}

export interface GameState {
  scoreUs: number;
  scoreThem: number;
  currentSet: number;
  isServing: boolean;
  rotation: Player[];
  substitutions: number;
  timeOuts: number;
  history: GameEvent[];
}

export interface GameEvent {
  id: string;
  type: string;
  player?: string;
  result?: string; // Point, Faute, Continuer
  score?: [number, number];
  timestamp: number;
}

export interface VolleyRow {
  id: string;
  set: number;
  /** Sets à gagner du match de la ligne : une vue multi-matchs peut mélanger les formats. */
  setsToWinMatch?: number;
  /** Match of the row (absent from an API older than VOL-341). */
  matchId?: string;
  scoreUs: number;
  scoreThem: number;
  joueur: string;
  /** Our player's id (absent for opponent rows and from an API older than VOL-209). */
  playerId?: string;
  /** Jersey number worn in that match, either side (absent when unknown or from an older API). */
  playerNumber?: number;
  role: string;
  action: string;
  zone: string;
  startZone?: string;
  endZone?: string;
  phase: string;
  rotation?: string;
  courtPosition?: number;
  middleBlockerCall?: string;
  passType?: string;
  passTypeLabel?: string;
  serviceType?: string;
  isReversal?: boolean;
  qualite: string;
  note: number;
  heure: string;
  targetX?: number;
  targetY?: number;
  originX?: number;
  originY?: number;
  touchedByBlock?: boolean;
  /** Nombre de bloqueurs en face de l'attaque (0 à 3). Absent = non renseigné. */
  blockersCount?: number;
  isOpponent?: boolean;
  teamSide?: 'home' | 'away';
  videoTimecodeMs?: number;
}

export interface FilterState {
  sets: number[];
  joueurs: string[];
  roles: string[];
  actions: string[];
  zones: string[];
  qualites: string[];
  rotations: string[];
  startZones: string[];
  endZones: string[];
  teams: string[];
  /** First contact of the possession: `Réception`, `Défense` or `Freeball Reçue` (VOL-265). */
  firstContacts: string[];
  firstContactQualities: string[];
  setDestinations: string[];
}

export interface PlayerInfo {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  number?: number;
  rosterIndex?: number;
  isLibero?: boolean;
}

export interface LineupPosition {
  position: number;
  playerId: string;
  role?: string;
}

export interface SetLineupInfo {
  setNumber: number;
  startingLineup: LineupPosition[] | null;
}

/** In a scouting match, `scoreUs` is the home side (live and imported alike). */
export interface MatchSetScoreInfo {
  setNumber: number;
  scoreUs: number;
  scoreThem: number;
  /** Playing time in seconds, stoppages over 5 minutes left out; absent from an older API. */
  durationSeconds?: number | null;
}

/** A player on the match sheet, with the number worn that day. */
export interface MatchRosterInfo {
  id: string;
  name: string;
  number?: number;
  isLibero?: boolean;
}

/** A player on the opponent's sheet of a live two-team match. */
export interface OpponentRosterInfo {
  number: number;
  name?: string;
  isLibero?: boolean;
}

/** Starting jerseys of each side in one set (imported files): index i is zone i + 1. */
export interface SideLineupInfo {
  setNumber: number;
  home?: number[];
  away?: number[];
  homeLiberoNumber?: number;
  awayLiberoNumber?: number;
}

export interface StatsResponse {
  actions: VolleyRow[];
  players: PlayerInfo[];
  setLineups: SetLineupInfo[];
  setScores?: MatchSetScoreInfo[];
  /** Single match only: the match sheet. */
  matchRoster?: MatchRosterInfo[];
  /** Single match only: the opponent's sheet (live two-team entry). */
  opponentRoster?: OpponentRosterInfo[];
  /** Single match only: starting jerseys of both sides per set (imported files). */
  sideLineups?: SideLineupInfo[];
  /** Sets à gagner, présent quand la réponse porte sur un seul match. */
  setsToWinMatch?: number;
  videoStartOffsetMs?: number;
  videoUrl?: string;
  /** Matches the stats were computed on (optional: absent from an API older than VOL-172). */
  matchCount?: number;
  /** The server cap was reached: only the most recent matches were counted. */
  matchCapReached?: boolean;
}

/** What the server aggregated for the Stats cumul view or a multi-match selection (VOL-172). */
export interface AggregateStatsInfo {
  matchCount: number;
  matchCapReached: boolean;
  /** Selected matches left out because their team closed stats to players (VOL-198). */
  closedExcludedCount?: number;
}

// Canonical definitions live in @/common/model/types — re-exported here for backwards compatibility.
import { CHAMPIONSHIPS, type ChampionshipLevel } from '@/common/model/types';
export { CHAMPIONSHIPS, type ChampionshipLevel };

export const DEFAULT_TEAM_CHAMPIONSHIP_LEVEL: ChampionshipLevel = 'Nationale 3';

export type TeamGender = 'M' | 'F' | 'Mixte';

export interface Team {
    id: string;
    name: string;
    gender: TeamGender;
}

export interface OpponentPlayer {
    id: string;
    name?: string;
    number: number;
    role?: string;
    createdAt?: string;
    /** Date of the first match where the record has an action (VOL-210). */
    firstPlayedAt?: string | null;
    /** Date of the latest match where the record has an action: tells the current wearer of a number (VOL-210). */
    lastPlayedAt?: string | null;
    /** Actions of past matches attributed to the record. */
    actionCount?: number;
}

export interface OpponentTeam {
    id: string;
    name: string;
    gender?: TeamGender;
    /** Maillot domicile, hexadécimal « #rrggbb ». */
    jerseyColor?: string | null;
    /** Maillot extérieur ; à défaut, le maillot domicile sert partout. */
    jerseyColorAway?: string | null;
    season?: string;
    /** Saved tactics (VOL-162), roster ids = opponent sheets; read with `toTeamTactics`. */
    teamTactics?: unknown;
    userId: string;
    clubId?: string;
    players: OpponentPlayer[];
    _count?: { players: number };
    createdAt: string;
    updatedAt: string;
}

// --- Nouveaux Types Métier (Domain Driven) ---

export type ActionOutcome = 'POINT' | 'FAULT' | 'CONTINUE';

export type ComplexPhase = 'K1' | 'K2' | 'K3'; // Side-out, Break-point, Transition

export type RotationPosition = 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6';

export interface Rally {
  id: string;
  setId: string;
  startTime: number;
  endTime?: number;
  startScoreUs: number;
  startScoreThem: number;
  servingTeam: 'US' | 'THEM';
  rotationUs: RotationPosition;
  actions: GameEvent[]; // Ou une structure Action plus détaillée
  winner?: 'US' | 'THEM';
  complex: ComplexPhase;
}

export interface DetailedAction extends GameEvent {
    phase: ComplexPhase;
    quality: string; // #, +, !, -, /, =
    startZone?: string;
    endZone?: string;
    skill: string; // Service, Reception, Attack, Block, Dig, Set
}

export interface RosterPlayer {
    id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    role: string;
    number?: number;
}

export interface TeamTacticsDto {
    p1AttackZone2: 'R4' | 'Pointu';
    liberoDefenseZone: 5 | 6;
    centralDefenseZone: 5 | 6;
    liberoReplaces?: string[];
    gameSystem?: '5-1' | '5-1-3R4' | '5-1-3C' | '5-1-1C' | '4-2' | '6-2';
    liberoPair?: { reception: string; defense: string };
}

/** What a player badge shows on the live court (VOL-113); `full` = number + name + role. */
export type PlayerBadgeContent = 'full' | 'number' | 'numberName' | 'numberInitials' | 'numberRole';

/** Player badges chosen by the coach for the whole team (VOL-113). Missing keys = defaults. */
export interface PlayerBadgeSettings {
    content?: PlayerBadgeContent;
    /** One colour per role, « #rrggbb »; a missing role keeps the default palette. */
    roleColors?: Partial<Record<'Passeur' | 'R4' | 'Central' | 'Pointu' | 'Lib', string>>;
}

export interface TeamDomain {
    id: string;
    name: string;
    level?: string;
    gender?: TeamGender;
    /** Saison de rattachement, ex. « 2026/2027 ». Sert à lever l'ambiguïté
     *  entre deux équipes homonymes de saisons différentes. */
    season?: string | null;
    roster: RosterPlayer[];
    teamTactics?: TeamTacticsDto;
    /** Maillot domicile, hexadécimal « #rrggbb ». */
    jerseyColor?: string | null;
    /** Maillot extérieur ; à défaut, le maillot domicile sert partout. */
    jerseyColorAway?: string | null;
    /** Photo / logo de l'équipe (URL publique). */
    logoUrl?: string | null;
    /** Équipe en lecture seule (au-delà du quota du plan). */
    locked?: boolean;
    /** Past season kept read-only by the team owner's plan (VOL-195); absent = false. */
    pastSeasonReadOnly?: boolean;
    /** Set with pastSeasonReadOnly: the viewer is the paying owner, the only one who can change plan. */
    viewerOwnsPlan?: boolean;
    /** Shared demo team (no club, no owner), decided by the backend (VOL-319). */
    isDemo?: boolean;
    /** The viewer may delete the team: its club or its owner (VOL-319); absent = allowed. */
    viewerCanDelete?: boolean;
    /** Les joueurs voient les matchs de l'équipe (VOL-178, défaut vrai). */
    playersSeeOwnMatches?: boolean;
    /** Les joueurs voient les matchs de scouting (VOL-178, défaut vrai). */
    playersSeeScoutingMatches?: boolean;
    /** Les joueurs de l'équipe accèdent aux stats (réglé par le coach). Absent = ouvert. */
    playerStatsAccess?: boolean;
    /** Les joueurs de l'équipe accèdent à la vidéo (réglé par le coach). Absent = ouvert. */
    playerVideoAccess?: boolean;
    /** Player badges on the live court (VOL-113); null = defaults. */
    playerBadgeSettings?: PlayerBadgeSettings | null;
    /** Team of an earlier season this one was carried over from (VOL-199); null = created by hand. */
    carriedOverFromId?: string | null;
}

export interface BackendTeamPlayer {
    id: string | number;
    name?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    /** `null` for a player without a number (e.g. number taken by a new player at import). */
    number?: number | null;
}

export interface BackendTeam {
    id: string | number;
    name: string;
    level?: string;
    gender?: TeamGender;
    clubId?: string | null;
    club?: Club | null;
    /** Saison de l'équipe, ex. « 2026/2027 » — renvoyée par `GET /teams`. */
    season?: string | null;
    players?: BackendTeamPlayer[];
    teamTactics?: TeamTacticsDto;
    /** Maillot domicile, hexadécimal « #rrggbb ». */
    jerseyColor?: string | null;
    /** Maillot extérieur ; à défaut, le maillot domicile sert partout. */
    jerseyColorAway?: string | null;
    /** Photo / logo de l'équipe (URL publique). */
    logoUrl?: string | null;
    /** Équipe en lecture seule (au-delà du quota du plan). */
    locked?: boolean;
    /** Past season kept read-only by the team owner's plan (VOL-195); absent = false. */
    pastSeasonReadOnly?: boolean;
    /** Set with pastSeasonReadOnly: the viewer is the paying owner, the only one who can change plan. */
    viewerOwnsPlan?: boolean;
    /** Shared demo team (no club, no owner), decided by the backend (VOL-319). */
    isDemo?: boolean;
    /** The viewer may delete the team: its club or its owner (VOL-319); absent = allowed. */
    viewerCanDelete?: boolean;
    /** Les joueurs voient les matchs de l'équipe (VOL-178, défaut vrai). */
    playersSeeOwnMatches?: boolean;
    /** Les joueurs voient les matchs de scouting (VOL-178, défaut vrai). */
    playersSeeScoutingMatches?: boolean;
    /** Les joueurs de l'équipe accèdent aux stats (réglé par le coach). Absent = ouvert. */
    playerStatsAccess?: boolean;
    /** Les joueurs de l'équipe accèdent à la vidéo (réglé par le coach). Absent = ouvert. */
    playerVideoAccess?: boolean;
    playerBadgeSettings?: PlayerBadgeSettings | null;
    /** Team of an earlier season this one was carried over from (VOL-199), even renamed since. */
    carriedOverFromId?: string | null;
}

/** `GET /teams/player-access`: player settings of every team the user can see. */
export interface TeamPlayerAccess {
    teamId: string;
    /** Member of the team itself, not only of its club. */
    isMember: boolean;
    playerStatsAccess: boolean;
    playerVideoAccess: boolean;
    /** Roster entry the coach linked to this account in the team (VOL-341). */
    linkedPlayerId: string | null;
}

/**
 * `GET /teams/players/:playerId/seasons`: the same person's roster entry in each season of
 * their team (VOL-341, DOMAIN_RULES B8), newest season first.
 */
export interface PlayerSeason {
    season: string;
    teamId: string;
    teamName: string;
    playerId: string;
    playerName: string;
    /** `self`: the entry asked for; `account`: same linked account; `name`: same name. */
    matchedBy: 'self' | 'account' | 'name';
}

/** `GET /teams/players/:playerId/seasons` (VOL-341, B8). */
export interface PlayerSeasons {
    seasons: PlayerSeason[];
    /** Teams of another name where the same person is found, left out of the history (renamed team, move). */
    elsewhere: { season: string; teamId: string; teamName: string }[];
}

/** `GET /teams/active-selection`: a team counted in the plan quota, with what helps choose it. */
export interface BackendTeamSelectionCandidate {
    id: string;
    name: string;
    level: string | null;
    season: string;
    locked: boolean;
    createdAt: string;
    playerCount: number;
    /** Own matches only (scouting excluded). */
    matchCount: number;
    lastMatchDate: string | null;
    /** A match of the team is being recorded: the backend refuses to lock it. */
    liveMatch: boolean;
    /** Last voluntary swap that changed this team's lock (7-day delay, VOL-193). */
    lockSwappedAt: string | null;
}

export interface TeamCreatePayload {
    name: string;
    level?: string;
    gender?: TeamGender;
}

export interface TeamUpdatePayload {
    name?: string;
    level?: string;
    gender?: TeamGender;
    roster?: RosterPlayer[];
    teamTactics?: TeamTacticsDto;
    /** Maillot domicile, hexadécimal « #rrggbb ». `null` efface la couleur. */
    jerseyColor?: string | null;
    /** Maillot extérieur, hexadécimal « #rrggbb ». `null` efface la couleur. */
    jerseyColorAway?: string | null;
    /** Photo de l'équipe (URL publique). `null` retire la photo. */
    logoUrl?: string | null;
    playersSeeOwnMatches?: boolean;
    playersSeeScoutingMatches?: boolean;
    playerStatsAccess?: boolean;
    playerVideoAccess?: boolean;
    /** Player badges on the live court (VOL-113). `null` resets to the defaults. */
    playerBadgeSettings?: PlayerBadgeSettings | null;
}

/** Dépôt présigné de la photo d'équipe (même contrat que la vidéo de match). */
export interface TeamLogoUploadUrlResponse {
    uploadUrl: string;
    key: string;
    publicUrl: string;
    contentType: string;
    expiresInSeconds: number;
    maxFileSizeBytes: number;
}

export interface PlayerCreatePayload {
    name?: string;
    firstName?: string;
    lastName?: string;
    role: string;
    number?: number;
}

export interface PlayerUpdatePayload {
    name?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    number?: number;
}

export interface LoginResponse {
    access_token: string;
    user: UserProfile;
}

export interface RegisterResponse {
    access_token?: string;
    user?: UserProfile;
    message?: string;
}

export interface ImpersonateResponse {
    access_token: string;
    user: UserProfile;
}

export interface CreateClubAndAdminResponse {
    clubId: string;
    invitationLink: string;
}

export interface InvitationResponse {
    token: string;
    link: string;
    role: string;
}

export interface InvitationLinkResponse {
    url: string;
    token?: string;
    expiresAt?: string;
}

/** `GET /users/terms` (VOL-199): documents to accept again, current versions. */
export interface TermsStatus {
    pending: ('CGU' | 'CGV')[];
    versions: { cgu: string; cgv: string };
}

/** `POST /users/terms`: the versions accepted, with what the app showed (language, box sentences). */
export interface AcceptTermsPayload {
    cguVersion?: string;
    cgvVersion?: string;
    language: string;
    cguText?: string;
    cgvText?: string;
}

export interface InvitationLinkPayload {
    target: string;
    role: string;
    clubId?: string;
    teamId?: string;
    /** Players under 15: the club collected a parent's consent for each (VOL-326). */
    minorParentalConsent?: boolean;
}

export interface ManagedTeam {
    id: string;
    name: string;
    season: string;
    clubId: string | null;
}

/** Roster entry a member account can be linked to (VOL-341). */
export interface LinkablePlayer {
    id: string;
    firstName: string | null;
    lastName: string | null;
    name: string | null;
    number: number | null;
}

export interface ManagedTeamPlayer extends LinkablePlayer {
    /** Account already linked to this entry, if any. */
    linkedUserId: string | null;
}

/** A team of the caller's scope, with the roster offered when linking an account. */
export interface ManagedScopeTeam extends ManagedTeam {
    players: ManagedTeamPlayer[];
}

export interface ManagedMemberTeam extends ManagedTeam {
    /** False for the team creator, who must keep his team. */
    canRemove: boolean;
    linkedPlayer: LinkablePlayer | null;
    /** Only PLAYER accounts get a roster entry. */
    canLinkPlayer: boolean;
}

export interface ManagedMember {
    id: string;
    email: string;
    name: string | null;
    role: Role;
    /** Teams within the caller's scope only. */
    teams: ManagedMemberTeam[];
    isClubOwner: boolean;
    inClub: boolean;
    canManage: boolean;
    /** Player under 15 whose parent's consent the club holds (VOL-326); absent from an older API. */
    minorParentalConsentAt?: string | null;
    /** The viewer may state or withdraw it: a manageable player account. */
    canDeclareMinor?: boolean;
}

export interface ManagedMembersResponse {
    members: ManagedMember[];
    teams: ManagedScopeTeam[];
    canRemoveFromClub: boolean;
}

export type DvwDialect = 'DATAVOLLEY' | 'CLICKNSCOUT' | 'VOLLEYMETRICS' | 'UNKNOWN';

export interface ScoutedOpponentApiRow {
  id: string;
  name: string;
  gender: string | null;
  clubId: string | null;
  scoutingMatchCount: number;
  lastScoutedAt: string;
}

export interface PaginatedScoutedOpponentsResponse {
  data: ScoutedOpponentApiRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScoutingMatchApiRow {
  id: string;
  date: string;
  opponent: string;
  location: string | null;
  championship: string | null;
  scoreUs: number;
  scoreThem: number;
  isFinished: boolean;
  kind: MatchKind | string;
  statTarget: string;
  teamId: string | null;
  homeOpponentTeamId: string | null;
  awayOpponentTeamId: string | null;
  homeOpponentTeamName: string | null;
  awayOpponentTeamName: string | null;
  createdAt: string;
}

export interface PaginatedScoutingMatchesResponse {
  data: ScoutingMatchApiRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface OpponentActionBucket {
  type: string;
  quality: string;
  count: number;
}

export interface OpponentAggregateStatsResponse {
  buckets: OpponentActionBucket[];
  totalOpponentActions: number;
}

export interface TopOpponentPlayerEntry {
  opponentPlayerId: string;
  number: number;
  name: string | null;
  total: number;
  success: number;
  errors: number;
  matchesPlayed: number;
  successRate: number;
  avgPerMatch: number;
}

export interface OpponentTopPlayersResponse {
  servers: TopOpponentPlayerEntry[];
  attackers: TopOpponentPlayerEntry[];
  receivers: TopOpponentPlayerEntry[];
  blockers: TopOpponentPlayerEntry[];
}

/** Opponent player named in a game plan (VOL-101): serve target or player to watch. */
export interface GamePlanPlayer {
  opponentPlayerId: string | null;
  number: number;
  name: string | null;
  note: string;
}

/** Court zone (1-9, DataVolley numbering) the opponent likes to attack from. */
export interface GamePlanAttackZone {
  zone: number;
  note: string;
}

/** Instructions for one of our rotations (rotation = setter position, 1-6 = P1-P6). */
export interface GamePlanRotation {
  rotation: number;
  /** Court slots in the reception line, in this rotation (P1 = 0 … P6 = 5). */
  receivers: number[];
  reception: string;
  attacks: string;
  middleCall: string;
}

export interface GamePlanRotations {
  /** Our starting six in rotation P1, by court slot (P1 = 0 … P6 = 5); `null` = empty slot. */
  lineup: (string | null)[];
  /** Libero in reception: shown in place of the back-row player she replaces. */
  libero: string | null;
  items: GamePlanRotation[];
}

export interface GamePlanContent {
  serveTargets: GamePlanPlayer[];
  watchPlayers: GamePlanPlayer[];
  attackZones: GamePlanAttackZone[];
  notes: string | null;
  rotations: GamePlanRotations | null;
  /** Day of the planned match, `YYYY-MM-DD` (VOL-342 lot 7); absent from an older API. */
  matchDate?: string | null;
}

export interface GamePlan extends GamePlanContent {
  id: string;
  teamId: string;
  opponentTeamId: string;
  updatedAt: string;
}

export interface GamePlanPlayerSuggestion {
  opponentPlayerId: string;
  number: number;
  name: string | null;
  reason: 'attack' | 'serve' | 'reception';
  total: number;
  /** Attack/serve: % of points (#). Reception: % positive (# and +). */
  rate: number;
}

export interface GamePlanZoneSuggestion {
  zone: number;
  count: number;
  share: number;
}

export interface GamePlanSuggestions {
  serveTargets: GamePlanPlayerSuggestion[];
  watchPlayers: GamePlanPlayerSuggestion[];
  /** Empty while too few attacks have a known start zone. */
  attackZones: GamePlanZoneSuggestion[];
  locatedAttacks: number;
}

/** A saved plan in the team's list (VOL-101 lot 4): players read it too. */
export interface GamePlanSummary {
  opponentTeamId: string;
  opponentName: string;
  /** Day of the planned match, `YYYY-MM-DD`; absent from an API older than VOL-342 lot 7. */
  matchDate?: string | null;
  updatedAt: string;
}

export interface GamePlanEnvelope {
  opponentName: string;
  plan: GamePlan | null;
  suggestions: GamePlanSuggestions;
}

export interface OpponentRosterPlayerRow {
  id: string;
  primaryNumber: number;
  alternativeNumbers: number[];
  fullName: string | null;
  role: string | null;
  matchesPlayed: number;
  actionsCount: number;
  lastSeenAt: string;
}

export interface PaginatedOpponentRosterResponse {
  data: OpponentRosterPlayerRow[];
  total: number;
  limit: number;
  offset: number;
}

export type MatchImportStatus =
    | 'PENDING'
    | 'PARSING'
    | 'READY'
    | 'PROCESSING'
    | 'CONFIRMED'
    | 'FAILED'
    | 'EXPIRED';

export type MatchKindImport = 'OWN' | 'SCOUTING';

export interface PlayerProposal {
    provisionalId: string;
    jerseyNumber: number;
    displayName: string;
    roleCode?: string;
    isLibero?: boolean;
    mapsToExistingPlayerId?: string;
}

export interface TeamProposal {
    side: 'home' | 'away';
    name: string;
    players: PlayerProposal[];
    mapsToExistingOpponentTeamId?: string;
}

export interface DraftAttackCombination {
    code: string;
    codeNormalized: string;
    zone?: number;
    side: string;
    tempo?: string;
    attackerType?: string;
    isBackRow: boolean;
    label: string;
    dvGridCoord?: number;
    colorRgb?: number;
    mapsToGlobalCombinationId?: string;
    source?: 'file' | 'seed' | 'synthetic';
}

export interface ImportDraftWarning {
    code: string;
    message: string;
    lineNumber?: number;
    rawLine?: string;
}

export interface DraftAction {
    setNumber: number;
    sequence: number;
    teamSide: 'home' | 'away';
    type: string;
    quality: string;
    playerProvisionalId?: string;
    startZone?: string;
    startSubzone?: string;
    endZone?: string;
    endSubzone?: string;
    endCone?: number;
    attackCombinationCode?: string;
    videoTimecodeMs?: number;
    rotation?: string;
    phase?: string;
}

export interface DraftSet {
    setNumber: number;
    scoreHome: number;
    scoreAway: number;
    durationSeconds?: number;
    homeStartingLineup?: unknown;
    awayStartingLineup?: unknown;
    homeLiberoNumber?: number;
    awayLiberoNumber?: number;
    actions: DraftAction[];
}

export interface DraftMiddleBlockerCall {
    code: string;
    codeNormalized: string;
    label: string;
    colorRgb?: number;
    secondaryColorRgb?: number;
    primaryCoord?: number;
    secondaryCoord?: number;
    tertiaryCoord?: number;
    multiCoords?: number[];
}

export interface MatchImportDraft {
    ownerTeamId: string;
    dialect: DvwDialect;
    matchLabel?: string;
    venue?: string;
    scheduledAt?: string;
    /** Other reading of an ambiguous file date (day and month both ≤ 12). */
    scheduledAtAlternative?: string;
    /** Why `scheduledAt` was preferred to the alternative; present with it. */
    scheduledAtReason?: 'FILE_SEASON' | 'LAST_CHANGE' | 'TODAY' | 'DIALECT';
    /** The date as written in the file, present with the alternative. */
    scheduledAtRaw?: string;
    season?: string;
    videoStartOffsetMs?: number;
    teams: TeamProposal[];
    sets: DraftSet[];
    attackCombinations: DraftAttackCombination[];
    middleBlockerCalls?: readonly DraftMiddleBlockerCall[];
    warnings: readonly ImportDraftWarning[];
}

export interface DvwImportInput {
    file: File;
    ownerTeamId?: string;
    newOwnTeamName?: string;
    newOwnTeamLevel?: string;
    newOwnTeamGender?: 'M' | 'F' | 'Mixte';
    newOwnTeamNumber?: number;
    matchKind: MatchKindImport;
    dialectOverride?: DvwDialect;
    encodingOverride?: string;
    scoutingFileHomeSide?: 'home' | 'away';
}

export interface TeamMappingChoice {
    mode: 'OWN_TEAM' | 'EXISTING_OPPONENT' | 'NEW_OPPONENT';
    opponentTeamId?: string;
    newOpponentName?: string;
}

export interface TeamSideMappingDto {
    kind: 'ownTeam' | 'opponentTeam' | 'newOpponent';
    id?: string;
    name?: string;
    clubId?: string;
}

export interface PlayerMappingEntryDto {
    dvwTeamSide: 'H' | 'V';
    dvwNumber: number;
    action: 'match' | 'create' | 'skip';
    targetPlayerId?: string;
    /** "create" on our team: roster player who loses this jersey number to the new player. */
    releaseNumberFromPlayerId?: string;
}

export interface ConfirmImportRequestDto {
    homeTeamMapping: TeamSideMappingDto;
    awayTeamMapping: TeamSideMappingDto;
    playerMappings: PlayerMappingEntryDto[];
    championship?: string;
    /** Match date chosen by the user (YYYY-MM-DD), sent only when it differs from the file's. */
    scheduledAt?: string;
    videoUrl?: string;
}

export interface UploadDvwResponse {
    draftId: string;
    status: MatchImportStatus;
    ownerTeamId?: string;
    createdFromImport?: boolean;
}

export interface GetImportStatusResponse {
    draftId?: string;
    id?: string;
    status: MatchImportStatus;
    draft?: MatchImportDraft;
    warnings?: readonly ImportDraftWarning[];
    errorCode?: string | null;
    errorMessage?: string | null;
    /** A confirmation refusal the coach can fix, as a code and its values (VOL-202). */
    errorDetail?: { code: string; params: Record<string, string | number> } | null;
    matchId?: string | null;
    progress?: number | null;
    dialect?: DvwDialect;
    format?: string;
    fileName?: string | null;
}

export interface ConfirmDvwResponse {
    draftId: string;
    status: 'PROCESSING';
}

export interface MatchExistsResponse {
    exists: boolean;
    matchId?: string;
}

export interface ApiError {
    code: string;
    message: string;
    details?: unknown;
}

export class TeamConflictError extends Error {
    readonly isConflict = true;
    readonly existingTeamId: string;
    readonly existingTeamName: string;

    constructor(message: string, existingTeamId: string, existingTeamName: string) {
        super(message);
        this.name = 'TeamConflictError';
        this.existingTeamId = existingTeamId;
        this.existingTeamName = existingTeamName;
    }
}
